import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import Sidebar from "@/components/Sidebar";
import { useTheme } from "@/context/ThemeContext";

// Embed configurations
const EMBED_CONFIGS = {
  resources: {
    url: "https://creditfirminc.net",
    title: "Credit Firm Resources",
    description: "Credit repair resources and information"
  },
  booking: {
    url: "https://api.leadconnectorhq.com/widget/booking/rRjZ8y0p56UjmV5DCtF1",
    title: "Book with Counselor",
    description: "Schedule a consultation with a credit counselor"
  }
};

const EmbedPage = ({ type = "resources", isAdmin = true }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const config = EMBED_CONFIGS[type] || EMBED_CONFIGS.resources;
  const basePath = isAdmin ? "/admin" : "/team";

  return (
    <div className={`flex min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-black' : 'bg-slate-50'}`}>
      {/* Sidebar - fixed position, does not take layout space on desktop */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={isAdmin} />

      {/* Main content - needs left margin on desktop to account for fixed sidebar */}
      <main className="flex-1 md:ml-64 flex flex-col h-screen">
        {/* Header */}
        <header className={`border-b px-4 md:px-6 py-3 flex-shrink-0 transition-colors duration-300 ${isDarkMode ? 'bg-black border-[#1a3d18]' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className={`md:hidden p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20 text-white' : 'hover:bg-slate-100'}`}
              >
                <Menu className="h-5 w-5" />
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`${basePath}/dashboard`)}
                className={`transition-colors ${isDarkMode ? 'text-slate-300 hover:text-white hover:bg-[#22c55e]/20' : 'text-slate-600 hover:text-slate-900'}`}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <h1 className={`text-lg font-semibold transition-colors ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{config.title}</h1>
                <p className={`text-xs transition-colors ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{config.description}</p>
              </div>
              <a
                href={config.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20 text-slate-300 hover:text-white' : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'}`}
                title="Open in new tab"
              >
                <ExternalLink className="h-5 w-5" />
              </a>
            </div>
          </div>
        </header>

        {/* Iframe Container - full width */}
        <div className={`flex-1 overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
          <iframe
            src={config.url}
            title={config.title}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </main>
    </div>
  );
};

export default EmbedPage;
